import { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { Table } from "react-bootstrap";
import type { RouteLineSummary } from "../RouteLineSummary";
import TableActionButton from "../../../../../@ui/TableActionButton";

interface RouteLineTableProps {
  summaries: RouteLineSummary[];
  onView: (ids: number[]) => void;
}

const PAGE_SIZE = 5;

const RouteLineTable = ({ summaries, onView }: RouteLineTableProps) => {
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [summaries]);

  const uniqueSorted = useMemo(() => {
    const groups = new Map<
      string,
      { ids: number[]; serviceNumber: string; operatorNames: string[] }
    >();
    for (const s of summaries) {
      if (!groups.has(s.serviceNumber)) {
        groups.set(s.serviceNumber, {
          ids: [],
          serviceNumber: s.serviceNumber,
          operatorNames: [],
        });
      }
      const group = groups.get(s.serviceNumber)!;
      if (!group.ids.includes(s.id)) {
        group.ids.push(s.id);
      }
      if (!group.operatorNames.includes(s.operatorName)) {
        group.operatorNames.push(s.operatorName);
      }
    }
    return Array.from(groups.values()).sort((a, b) =>
      a.serviceNumber.localeCompare(b.serviceNumber),
    );
  }, [summaries]);

  const totalPages = Math.max(1, Math.ceil(uniqueSorted.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages - 1);
  const pageItems = uniqueSorted.slice(
    currentPage * PAGE_SIZE,
    currentPage * PAGE_SIZE + PAGE_SIZE,
  );

  return (
    <>
      <Table striped bordered hover size="sm" className="mb-0 text-center">
        <thead>
          <tr>
            <th style={{ width: "1px" }}>Service</th>
            <th>Operator</th>
            <th style={{ width: "1px" }}></th>
          </tr>
        </thead>
        <tbody>
          {pageItems.map((s) => (
            <tr key={s.serviceNumber}>
              <td>{s.serviceNumber}</td>
              <td>{s.operatorNames.join(" / ")}</td>
              <td>
                <TableActionButton onClick={() => onView(s.ids)}>
                  <FontAwesomeIcon icon={faEye} />
                </TableActionButton>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {totalPages > 1 && (
        <div className="d-flex align-items-center justify-content-between px-2 py-1 border-top">
          <TableActionButton
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={currentPage === 0}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </TableActionButton>
          <small className="text-muted">
            {currentPage + 1} / {totalPages}
          </small>
          <TableActionButton
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={currentPage === totalPages - 1}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </TableActionButton>
        </div>
      )}
    </>
  );
};

export default RouteLineTable;
